import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { apiService } from "../../services/apiService";
import Select from "react-select";

const InvoiceList = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [editFormData, setEditFormData] = useState({
    status: '',
    notes: ''
  });
  const [editLoading, setEditLoading] = useState(false);

  // Status choices for the select dropdown
  const STATUS_CHOICES = [
    { value: 'not_started', label: 'Not Started' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
  ];

  const fetchInvoices = async () => {
    setLoading(true);
    const response = await apiService.get("accounts/invoice/list/");
    
    if (Array.isArray(response)) {
      setInvoices(response);
    } else {
      setInvoices([]);
    }
    setLoading(false);
  };

  const updateInvoice = async (invoiceId, updateData) => {
    const response = await apiService.patch(`accounts/invoice/${invoiceId}/update/`, updateData);
    console.log("Updated invoice:", response);
    return response;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      not_started: { class: "bg-secondary", text: "Not Started" },
      pending: { class: "bg-warning", text: "Pending" },
      in_progress: { class: "bg-info", text: "In Progress" },
      completed: { class: "bg-success", text: "Completed" },
      cancelled: { class: "bg-danger", text: "Cancelled" },
    };

    const config = statusConfig[status] || {
      class: "bg-secondary",
      text: "Unknown",
    };
    return <span className={`badge ${config.class}`}>{config.text}</span>;
  };

  const handleUpdateInvoice = async (invoice) => {
    const updateData = {
      status: "in_progress",
      notes: "Updated via API"
    };
    
    const updatedInvoice = await updateInvoice(invoice.id, updateData);
    toast.success(`Invoice ${invoice.id} updated successfully`);
    fetchInvoices();
  };

  // Edit modal handlers
  const handleOpenEditModal = (invoice) => {
    setEditInvoice(invoice);
    setEditFormData({
      status: invoice.status || '',
      notes: invoice.notes || ''
    });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditInvoice(null);
    setEditFormData({
      status: '',
      notes: ''
    });
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUpdateInquiry = async () => {
    if (!editInvoice) return;
    
    setEditLoading(true);
    try {
      const updateData = {
        status: editFormData.status,
        notes: editFormData.notes
      };
      
      await updateInvoice(editInvoice.id, updateData);
      toast.success(`Invoice ${editInvoice.id} updated successfully`);
      handleCloseEditModal();
      fetchInvoices();
    } catch (error) {
      console.error('Error updating invoice:', error);
      toast.error('Failed to update invoice');
    } finally {
      setEditLoading(false);
    }
  };



  useEffect(() => {
    fetchInvoices();
  }, []);

  return (
    <div className="">
      <div className="page-header">
        <div className="row align-items-center">
          <div className="col">
            <h3 className="page-title">Invoice List</h3>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h4 className="card-title">Invoice Records</h4>
          <span className="badge bg-primary">Total: {invoices.length}</span>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted">
                No invoices found with the selected filters.
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped table-hover">
                <thead>
                  <tr>
                    <th>Sr.No</th>
                    <th>Firm Name</th>
                    <th>Mobile Number</th>
                    <th>Status</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{invoice.firm_name}</td>
                      <td>{invoice.mobile_number}</td>
                      <td>{getStatusBadge(invoice.status)}</td>
                      <td>{invoice.notes || "N/A"}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleOpenEditModal(invoice)}
                            title="Edit Invoice"
                          >
                            <i className="fe fe-edit"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && editInvoice && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Invoice</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseEditModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group mb-3">
                      <label htmlFor="status" className="form-label">Status</label>
                      <Select
                        options={STATUS_CHOICES}
                        value={STATUS_CHOICES.find(option => option.value === editFormData.status) || null}
                        onChange={(selectedOption) => {
                          handleEditFormChange('status', selectedOption ? selectedOption.value : '');
                        }}
                        isClearable
                        placeholder="Select status"
                        className="react-select-container"
                        classNamePrefix="react-select"
                      />
                    </div>
                    <div className="form-group mb-3">
                      <label htmlFor="notes" className="form-label">Notes</label>
                      <textarea
                        className="form-control"
                        id="notes"
                        rows="4"
                        value={editFormData.notes}
                        onChange={(e) => handleEditFormChange('notes', e.target.value)}
                        placeholder="Enter notes..."
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={handleCloseEditModal}
                  disabled={editLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleUpdateInquiry}
                  disabled={editLoading}
                >
                  {editLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Updating...
                    </>
                  ) : (
                    'Update Invoice'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceList;
